/**
 * Notification types
 * - RANKING_IMPROVED: Tour moved up in search results
 * - RANKING_DROPPED: Tour moved down in search results
 * - NOT_IN_RESULTS: Tour no longer appears in search results
 * - REVIEW_SPIKE: Significant change in review metrics (rating or count)
 * - STALE_DATA: No recent scrapes for keyword (48h threshold)
 */
export enum NotificationType {
  RANKING_IMPROVED = 'RANKING_IMPROVED',
  RANKING_DROPPED = 'RANKING_DROPPED',
  NOT_IN_RESULTS = 'NOT_IN_RESULTS',
  REVIEW_SPIKE = 'REVIEW_SPIKE',
  PRICE_INCREASE = 'PRICE_INCREASE',
  PRICE_DECREASE = 'PRICE_DECREASE',
  STALE_DATA = 'STALE_DATA',
}

/**
 * Notification severity levels
 * - INFO: Informational (minor ranking improvement)
 * - WARNING: Needs attention (significant drop, stale data)
 * - CRITICAL: Urgent (not found in results, major drop)
 */
export enum NotificationSeverity {
  INFO = 'INFO',
  WARNING = 'WARNING',
  CRITICAL = 'CRITICAL',
}

/**
 * Core notification metadata for different types
 */
export interface RankingNotificationMetadata {
  keywordId: string;
  platform: string;
  snapshotId: string;
  previousRank: number | null;
  currentRank: number | null;
}

export interface ReviewNotificationMetadata {
  discoveredTourId: string;
  platform: string;
  previousRating?: number;
  currentRating?: number;
  previousReviewCount?: number;
  currentReviewCount?: number;
}

export interface StaleDataNotificationMetadata {
  keywordId: string;
  lastScrapedAt?: string;
}

export type NotificationMetadata =
  | RankingNotificationMetadata
  | ReviewNotificationMetadata
  | StaleDataNotificationMetadata
  | Record<string, unknown>;

/**
 * In-app notification delivery record
 */
export interface NotificationInApp {
  id: string;
  notificationId: string;
  isRead: boolean;
  isDismissed: boolean;
  readAt: Date | null;
  createdAt: Date;
}

/**
 * Core notification entity
 */
export interface Notification {
  id: string;
  userId: string;
  tourId: string | null;
  type: NotificationType;
  title: string;
  message: string;
  severity: NotificationSeverity;
  metadata: NotificationMetadata | null;
  resolvedAt: Date | null;
  resolvedBy: string | null;
  createdAt: Date;

  // Relations (optional, populated by includes)
  inApp?: NotificationInApp;
  tour?: {
    id: string;
    name: string;
  };
}

/**
 * DTOs for API requests/responses
 */
export interface CreateNotificationDto {
  userId: string;
  tourId?: string;
  type: NotificationType;
  title: string;
  message: string;
  severity: NotificationSeverity;
  metadata?: NotificationMetadata;
}

export interface ListNotificationsParams {
  unreadOnly?: boolean;
  limit?: number;
  offset?: number;
}

export interface ListNotificationsResponse {
  notifications: Notification[];
  unreadCount: number;
  total: number;
}
